import { StockQuote, StockSearchResult } from '../types/investment'

// Cache for stock quotes to avoid excessive API calls
const quoteCache = new Map<string, { data: StockQuote; timestamp: number }>()
const CACHE_DURATION = 30000 // 30 seconds

class MarketDataService {
  private apiKey: string

  constructor() {
    this.apiKey = import.meta.env.VITE_FINNHUB_API_KEY
    if (!this.apiKey) {
      console.warn('Finnhub API key not found. Please add VITE_FINNHUB_API_KEY to your .env.local file')
      console.warn('Get your free API key from: https://finnhub.io/register')
    }
  }

  /**
   * Fetch current stock quote with caching
   */
  async fetchStockQuote(symbol: string): Promise<StockQuote> {
    const cacheKey = symbol.toUpperCase()
    const cached = quoteCache.get(cacheKey)
    
    // Return cached data if it's still fresh
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data
    }

    if (!this.apiKey) {
      console.warn('Using mock data - Finnhub API key not configured')
      return this.getMockStockQuote(symbol)
    }

    try {
      // Fetch quote data from Finnhub
      const quoteResponse = await fetch(
        `https://finnhub.io/api/v1/quote?symbol=${symbol.toUpperCase()}&token=${this.apiKey}`
      )
      
      if (!quoteResponse.ok) {
        throw new Error(`Failed to fetch quote for ${symbol}: ${quoteResponse.statusText}`)
      }

      const quoteData = await quoteResponse.json()
      
      // Check if we got valid data
      if (quoteData.c === 0 && quoteData.d === 0 && quoteData.dp === 0) {
        throw new Error(`No data available for ${symbol}`)
      }

      // Fetch company profile for company name
      let companyName = symbol.toUpperCase()
      try {
        const profileResponse = await fetch(
          `https://finnhub.io/api/v1/stock/profile2?symbol=${symbol.toUpperCase()}&token=${this.apiKey}`
        )
        
        if (profileResponse.ok) {
          const profileData = await profileResponse.json()
          if (profileData.name) {
            companyName = profileData.name
          }
        }
      } catch (error) {
        console.warn(`Failed to fetch company profile for ${symbol}:`, error)
      }

      const quote: StockQuote = {
        symbol: symbol.toUpperCase(),
        companyName,
        currentPrice: quoteData.c || 0,
        change: quoteData.d || 0,
        changePercent: quoteData.dp || 0,
        volume: 0, // Finnhub quote doesn't include volume
        marketCap: 0, // Will be fetched separately if needed
        lastUpdated: new Date().toISOString()
      }

      // Cache the result
      quoteCache.set(cacheKey, {
        data: quote,
        timestamp: Date.now()
      })

      console.log(`✅ Fetched quote for ${symbol}: $${quote.currentPrice} (${quote.changePercent}%)`)
      return quote
    } catch (error) {
      console.error(`Error fetching quote for ${symbol}:`, error)
      
      // Return cached data if available, even if stale
      if (cached) {
        console.log(`Using stale cached data for ${symbol}`)
        return cached.data
      }
      
      throw new Error(`Failed to fetch quote for ${symbol}: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Search for stocks by symbol or company name
   */
  async searchStocks(query: string): Promise<StockSearchResult[]> {
    if (!query.trim()) return []

    if (!this.apiKey) {
      console.warn('Using mock search results - Finnhub API key not configured')
      return this.getMockSearchResults(query)
    }

    try {
      const response = await fetch(
        `https://finnhub.io/api/v1/search?q=${encodeURIComponent(query)}&token=${this.apiKey}`
      )

      if (!response.ok) {
        throw new Error(`Search failed: ${response.statusText}`)
      }

      const data = await response.json()
      
      if (!data.result || !Array.isArray(data.result)) {
        return []
      }

      return data.result
        .filter((item: any) => item.symbol && item.description)
        .slice(0, 10) // Limit to 10 results
        .map((item: any) => ({
          symbol: item.symbol,
          name: item.description,
          exchange: item.type || 'Unknown',
          type: 'Stock'
        }))
    } catch (error) {
      console.error('Error searching stocks:', error)
      
      // Return mock data for demo purposes when API fails
      return this.getMockSearchResults(query)
    }
  }

  /**
   * Fetch historical data for charts (placeholder for future use)
   */
  async fetchHistoricalData(symbol: string, _period: string = '1mo'): Promise<any[]> {
    // This is a placeholder - Finnhub's historical data requires a paid plan
    // For now, return empty array
    console.warn(`Historical data not available for ${symbol} with free tier`)
    return []
  }

  /**
   * Get market hours status
   */
  getMarketHours(): { isOpen: boolean; nextOpen?: string; nextClose?: string } {
    const now = new Date()
    const easternTime = new Date(now.toLocaleString("en-US", {timeZone: "America/New_York"}))
    
    const hour = easternTime.getHours()
    const minute = easternTime.getMinutes()
    const currentTime = hour * 60 + minute
    
    const marketOpen = 9 * 60 + 30 // 9:30 AM
    const marketClose = 16 * 60 // 4:00 PM
    
    const isWeekday = easternTime.getDay() >= 1 && easternTime.getDay() <= 5
    const isOpen = isWeekday && currentTime >= marketOpen && currentTime < marketClose
    
    return {
      isOpen,
      nextOpen: !isOpen ? '09:30 AM ET' : undefined,
      nextClose: isOpen ? '04:00 PM ET' : undefined
    }
  }

  /**
   * Get mock stock quote for demo purposes
   */
  private getMockStockQuote(symbol: string): StockQuote {
    const mockPrices: { [key: string]: { price: number; change: number; changePercent: number } } = {
      'AAPL': { price: 175.43, change: 2.15, changePercent: 1.24 },
      'GOOGL': { price: 142.56, change: -1.23, changePercent: -0.86 },
      'MSFT': { price: 378.85, change: 3.42, changePercent: 0.91 },
      'TSLA': { price: 248.50, change: -5.20, changePercent: -2.05 },
      'AMZN': { price: 155.20, change: 1.85, changePercent: 1.21 },
      'NVDA': { price: 875.30, change: 12.45, changePercent: 1.44 },
      'META': { price: 485.75, change: -2.30, changePercent: -0.47 },
      'NFLX': { price: 485.75, change: 8.25, changePercent: 1.73 },
      'AMD': { price: 142.30, change: 1.20, changePercent: 0.85 },
      'INTC': { price: 45.60, change: -0.80, changePercent: -1.72 }
    }

    const stockData = mockPrices[symbol.toUpperCase()] || {
      price: 100 + Math.random() * 200,
      change: (Math.random() - 0.5) * 10,
      changePercent: (Math.random() - 0.5) * 5
    }

    const quote: StockQuote = {
      symbol: symbol.toUpperCase(),
      companyName: `${symbol.toUpperCase()} Inc.`,
      currentPrice: stockData.price,
      change: stockData.change,
      changePercent: stockData.changePercent,
      volume: Math.floor(Math.random() * 1000000),
      marketCap: Math.floor(stockData.price * 1000000000),
      lastUpdated: new Date().toISOString()
    }

    // Cache the mock data
    quoteCache.set(symbol.toUpperCase(), {
      data: quote,
      timestamp: Date.now()
    })

    console.log(`📊 Using mock quote for ${symbol}: $${quote.currentPrice} (${quote.changePercent}%)`)
    return quote
  }

  /**
   * Mock search results for demo purposes
   */
  private getMockSearchResults(query: string): StockSearchResult[] {
    const mockStocks = [
      { symbol: 'AAPL', name: 'Apple Inc.' },
      { symbol: 'GOOGL', name: 'Alphabet Inc.' },
      { symbol: 'MSFT', name: 'Microsoft Corporation' },
      { symbol: 'TSLA', name: 'Tesla, Inc.' },
      { symbol: 'AMZN', name: 'Amazon.com, Inc.' },
      { symbol: 'NVDA', name: 'NVIDIA Corporation' },
      { symbol: 'META', name: 'Meta Platforms, Inc.' },
      { symbol: 'NFLX', name: 'Netflix, Inc.' },
      { symbol: 'AMD', name: 'Advanced Micro Devices, Inc.' },
      { symbol: 'INTC', name: 'Intel Corporation' }
    ]

    const filtered = mockStocks.filter(stock =>
      stock.symbol.toLowerCase().includes(query.toLowerCase()) ||
      stock.name.toLowerCase().includes(query.toLowerCase())
    )

    return filtered.map(stock => ({
      symbol: stock.symbol,
      name: stock.name,
      exchange: 'NASDAQ',
      type: 'Stock'
    }))
  }

  /**
   * Clear cache (useful for testing)
   */
  clearCache(): void {
    quoteCache.clear()
  }

  /**
   * Get API status
   */
  getApiStatus(): { configured: boolean; apiKey: string } {
    return {
      configured: !!this.apiKey,
      apiKey: this.apiKey ? `${this.apiKey.substring(0, 8)}...` : 'Not configured'
    }
  }
}

// Export singleton instance
export const marketDataService = new MarketDataService()
